import React, { useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";

// Same category options you were using earlier (you can tweak if needed)
const VARIANT_CATEGORY_GROUPS = [
  {
    label: "Industry",
    options: [
      "Food",
      "Bakery",
      "Grocery",
      "Dairy",
      "Beverages",
      "Household",
      "Personal Care",
      "Pharmacy",
      "Pet Care",
      "Baby Care",
      "Stationery",
      "Home & Kitchen",
      "Cleaning",
      "Electronics",
      "Fashion",
      "Health & Wellness",
    ],
  },
  {
    label: "Food Types",
    options: [
      "Fruits & Vegetables",
      "Staples & Grains",
      "Rice & Flour",
      "Pulses & Lentils",
      "Oils & Ghee",
      "Masala & Spices",
      "Snacks & Namkeen",
      "Biscuits & Cookies",
      "Chocolates & Sweets",
      "Breakfast & Cereals",
      "Instant & Ready-to-Eat",
      "Bakery & Breads",
      "Cakes & Pastries",
      "Dairy & Cheese",
      "Paneer & Tofu",
      "Eggs & Meat",
      "Seafood",
      "Frozen Foods",
      "Beverages - Soft Drinks",
      "Beverages - Juices",
      "Tea & Coffee",
      "Water & Ice",
    ],
  },
];

const VariantManagerModal = ({
  show,
  onHide,
  baseUrl,
  config,
  variants = [],
  loadingVariants = false,
  refreshVariants, // function => fetchVariants from parent
}) => {
  const [form, setForm] = useState({
    variantId: "",
    variantTitle: "",
    category: "",
  });

  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const clearSuccess = () => setSuccessMessage("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    clearSuccess();
    setForm((f) => ({ ...f, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      variantId: "",
      variantTitle: "",
      category: "",
    });
    clearSuccess();
  };

  const handleCreate = async () => {
    clearSuccess();
    if (!form.variantTitle.trim()) {
      Swal.fire({ icon: "warning", title: "Variant title required" });
      return;
    }

    const payload = {
      variantTitle: form.variantTitle.trim(),
      category: form.category?.trim() || "",
    };

    try {
      setSaving(true);
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/insertInventoryVariant`,
        payload,
        config
      );
      const data = res.data;

      if (data?.responseCode === "00" || data?.code === 200) {
        setSuccessMessage("Variant added successfully.");
        resetForm();
        refreshVariants && refreshVariants();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: data?.responseMessage || "Could not add variant.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Could not add variant.",
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    clearSuccess();
    setForm({
      variantId: String(v.id),
      variantTitle: v.variant_title || "",
      category: v.category || "",
    });
  };

  const handleUpdate = async () => {
    clearSuccess();
    if (!form.variantId) return;

    if (!form.variantTitle.trim()) {
      Swal.fire({ icon: "warning", title: "Variant title required" });
      return;
    }

    const payload = {
      variantId: String(form.variantId),
      variantTitle: form.variantTitle.trim(),
      category: form.category?.trim() || "",
    };

    try {
      setSaving(true);
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/updateInventoryVariant`,
        payload,
        config
      );
      const data = res.data;

      if (data?.responseCode === "00" || data?.code === 200) {
        setSuccessMessage("Variant updated successfully.");
        resetForm();
        refreshVariants && refreshVariants();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: data?.responseMessage || "Could not update variant.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Could not update variant.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    clearSuccess();
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete variant?",
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!ok.isConfirmed) return;

    const payload = { variantId: String(id) };

    try {
      setSaving(true);
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/deleteInventoryVariant`,
        payload,
        config
      );
      const data = res.data;

      if (data?.responseCode === "00" || data?.code === 200) {
        setSuccessMessage("Variant deleted successfully.");
        refreshVariants && refreshVariants();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: data?.responseMessage || "Could not delete variant.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Could not delete variant.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onHide && onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fw-semibold">Variant Manager</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {successMessage && (
          <Alert
            variant="success"
            className="py-2 mb-3"
            onClose={clearSuccess}
            dismissible
          >
            {successMessage}
          </Alert>
        )}

        {/* Form Row */}
        <div className="mb-3">
          <Form>
            <div className="row g-3">
              <div className="col-md-5">
                <Form.Group controlId="variantTitle">
                  <Form.Label className="fw-semibold small">
                    Variant Title
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="variantTitle"
                    value={form.variantTitle}
                    onChange={handleChange}
                    placeholder="e.g. Regular, Large, Sugar Free"
                    size="sm"
                  />
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group controlId="variantCategory">
                  <Form.Label className="fw-semibold small">
                    Category (for variant)
                  </Form.Label>
                  <Form.Select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    size="sm"
                  >
                    <option value="">Select</option>
                    {VARIANT_CATEGORY_GROUPS.map((grp) => (
                      <optgroup key={grp.label} label={grp.label}>
                        {grp.options.map((opt) => (
                          <option value={opt} key={opt}>
                            {opt}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-md-3 d-flex align-items-end gap-2">
                {!form.variantId ? (
                  <Button
                    variant="success"
                    size="sm"
                    className="w-100 fw-semibold"
                    onClick={handleCreate}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Add Variant"
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-100 fw-semibold"
                    onClick={handleUpdate}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Updating...
                      </>
                    ) : (
                      "Update Variant"
                    )}
                  </Button>
                )}

                {form.variantId && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={resetForm}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </div>

        {/* List */}
        <div className="border rounded-3">
          <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
            <span className="fw-semibold small">Existing Variants</span>
            {loadingVariants && (
              <span className="small text-muted d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading...
              </span>
            )}
          </div>

          <div
            style={{
              maxHeight: "260px",
              overflowY: "auto",
            }}
          >
            {variants.length === 0 && !loadingVariants ? (
              <div className="px-3 py-3 small text-muted">
                No variants found. Add your first variant above.
              </div>
            ) : (
              <table className="table table-sm table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "40%" }}>Title</th>
                    <th style={{ width: "40%" }}>Category</th>
                    <th style={{ width: "20%" }} className="text-end">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => (
                    <tr key={v.id}>
                      <td>{v.variant_title}</td>
                      <td className="text-muted small">{v.category}</td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => startEdit(v)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(v.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="success"
          onClick={handleClose}
          disabled={saving}
          className="fw-semibold"
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VariantManagerModal;
